import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { ExpandMore } from "@mui/icons-material"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink } from "react-router-dom"
import { Locker } from "shared/locker"
import moment from "moment"
import { getLockerDisplayValues } from "src/hooks/useLockerDisplayValues"

export type TenantMigrationData = {
  tenantId: string
  tenantName: string
  v1Count: number
  v2Count: number
  totalCount: number
  v2Percentage: number
  lockers: Locker[]
}

export const TenantMigrationAccordion = React.memo<{
  tenant: TenantMigrationData
  expanded: boolean
  onToggle: () => void
}>(({ tenant, expanded, onToggle }) => {
  const { t } = useTranslation()

  return (
    <Accordion expanded={expanded} onChange={onToggle}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" spacing={2} alignItems="center" width="100%">
          <Stack direction="column" spacing={0.5} flex={1}>
            <Typography variant="body1" fontWeight="bold">
              {tenant.tenantName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tenant.tenantId}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`V1: ${tenant.v1Count}`}
              size="small"
              color={tenant.v1Count > 0 ? "info" : "default"}
              variant="outlined"
            />
            <Chip
              label={`V2: ${tenant.v2Count}`}
              size="small"
              color={tenant.v2Count > 0 ? "success" : "default"}
              variant="outlined"
            />
            <Typography variant="caption">
              {tenant.totalCount} {t("lockers")}
            </Typography>
          </Stack>
          <Box width={80}>
            <LinearProgress
              variant="determinate"
              value={tenant.v2Percentage}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "grey.300",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "success.main",
                },
              }}
            />
            <Typography variant="caption" textAlign="center" display="block">
              {tenant.v2Percentage.toFixed(0)}%
            </Typography>
          </Box>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("lockerName")}</TableCell>
                <TableCell>{t("sstLockerId")}</TableCell>
                <TableCell>{t("apiVersion")}</TableCell>
                <TableCell>{t("onlineStatus")}</TableCell>
                <TableCell>{t("lastSync")}</TableCell>
                <TableCell>{t("actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenant.lockers.map((locker) => {
                const displayValues = getLockerDisplayValues(locker)
                const isOnline =
                  locker.sstApiVersion === "v2"
                    ? locker?.sst2Locker?.is_online || false
                    : locker?.sstLocker?.is_online || false
                const apiVersion = locker.sstApiVersion || "v1"

                return (
                  <TableRow key={locker.id}>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {displayValues.i18n.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" noWrap>
                        {locker.sstLockerId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={apiVersion.toUpperCase()}
                        size="small"
                        color={apiVersion === "v2" ? "info" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: isOnline ? "green" : "red",
                          }}
                        />
                        <Typography variant="caption">
                          {t(isOnline ? "online" : "offline")}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {locker.lastSstSync
                          ? moment(locker.lastSstSync).fromNow()
                          : "never"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/sa/lockers/${locker.id}`}
                      >
                        {t("viewLocker")}
                      </Link>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  )
})

TenantMigrationAccordion.displayName = "TenantMigrationAccordion"

export default TenantMigrationAccordion
